import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useUser } from '@/contexts/UserContext';
import { hasFranchiseeActionAccess } from '@/helpers/orderPermissions';
import { cn } from '@/lib/utils';

import AddCroqui from '@/components/AddCroqui';
import AddMediaModal from '@/components/AddMediaModal';
import AddRecommendationCode from '@/components/AddRecommendationCode';
import AdvancedOptionsComponent from '@/components/AdvancedOptions/AdvancedOptionsComponent';
import Modal from '@/components/common/Modal';
import ForwardModal from '@/components/ForwardModal';
import ListBackup from '@/components/ListBackup';
import MakeEditable from '@/components/MakeEditable';
import Reactivation from '@/components/Reactivation';
import RemoveVNR from '@/components/RemoveVNR';
import UpdateAllowedAtDate from '@/components/UpdateAllowedAtDate';

const components = {
  makeEditable: {
    component: MakeEditable,
    feature: 'MAKE_ORDER_EDITABLE',
    role: 'ROLE_FRANCHISEE',
    extraStyles: { width: '520px', maxWidth: '520px', minWidth: '460px' },
  },
  addRecommendationCode: {
    component: AddRecommendationCode,
    extraStyles: { width: '480px', maxWidth: '480px', minWidth: '480px' },
  },
  list_backups: {
    component: ListBackup,
    extraStyles: { width: '820px', maxWidth: '820px', minWidth: '640px' },
  },
  updateAllowedAtDate: {
    component: UpdateAllowedAtDate,
  },
  removeVNR: {
    component: RemoveVNR,
    role: 'ROLE_FRANCHISEE',
  },
  reactivation: {
    component: Reactivation,
    role: 'ROLE_FRANCHISEE',
  },
  addCroqui: {
    component: AddCroqui,
    role: 'ROLE_FRANCHISEE',
  },
  addMedia: {
    component: AddMediaModal,
    role: 'ROLE_FRANCHISEE',
  },
  forward: {
    component: ForwardModal,
    role: 'ROLE_FRANCHISEE',
    className: 'sm:max-w-[500px] overflow-visible',
  },
};

const AdvancedOptionsContainer = ({
  orderFranchisee,
  orderId,
  orderStatus,
  orderType,
  refreshHandler,
  updateOrderItem,
  order,
}) => {
  const user = useUser();
  const userFranchisee = user?.franchiseeId;
  const role = user?.authRole;
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [advancedActions, setAdvancedActions] = useState([]);
  const [currentAction, setCurrentAction] = useState(null);
  const menuRef = useRef();

  const toggleMenu = () => setIsMenuVisible(!isMenuVisible);

  const handleClose = () => setCurrentAction(null);

  const selectOption = action => {
    setCurrentAction(action);
    setIsMenuVisible(false);
  };

  const renderAction = () => {
    const actionConfig = components[currentAction];
    const Component = actionConfig?.component;

    if (!Component) return null;

    const defaultExtra = { maxWidth: '425px' };
    const extraStyles = actionConfig?.extraStyles ? { ...defaultExtra, ...actionConfig.extraStyles } : defaultExtra;

    return (
      <Modal onClose={handleClose} extraStyles={extraStyles}>
        <div className={cn(actionConfig.className)}>
          <Component
            orderId={orderId}
            closeModal={handleClose}
            refreshHandler={refreshHandler}
            updateOrderItem={updateOrderItem}
          />
        </div>
      </Modal>
    );
  };

  // get advanced options from session storage
  useEffect(() => {
    const orderTypeConfigurations = JSON.parse(sessionStorage.getItem('orderTypeConfigurations') || null);
    const advancedActionsFromSession = orderTypeConfigurations?.[orderType]?.[orderStatus]?.advancedActions || [];

    if (userFranchisee !== orderFranchisee) {
      if (hasFranchiseeActionAccess(user, order)) {
        const allowedActions = advancedActionsFromSession.filter(action => ['makeEditable', 'list_backups'].includes(action));
        setAdvancedActions(allowedActions);
      } else {
        setAdvancedActions([]);
      }
      return;
    }

    setAdvancedActions(advancedActionsFromSession);
  }, [orderFranchisee, orderStatus, orderType, userFranchisee, user, order]);

  // click away events
  useEffect(() => {
    const closeOnClickAway = evt => {
      if (menuRef?.current?.contains(evt.target)) return;
      setIsMenuVisible(false);
    };

    const closeOnEsc = evt => {
      if (evt.key !== 'Escape') return;
      setIsMenuVisible(false);
    };

    document.addEventListener('mouseup', closeOnClickAway);
    document.addEventListener('keyup', closeOnEsc);

    return () => {
      document.removeEventListener('mouseup', closeOnClickAway);
      document.removeEventListener('keyup', closeOnEsc);
    };
  }, [menuRef]);

  return (
    <AdvancedOptionsComponent
      menuRef={menuRef}
      advancedActions={advancedActions}
      isMenuVisible={isMenuVisible}
      toggleMenu={toggleMenu}
      selectOption={selectOption}
      CurrentAction={renderAction}
      components={components}
      role={role}
    />
  );
};

AdvancedOptionsContainer.propTypes = {
  orderFranchisee: PropTypes.number.isRequired,
  orderId: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  orderStatus: PropTypes.string.isRequired,
  refreshHandler: PropTypes.func.isRequired,
  updateOrderItem: PropTypes.func,
};

AdvancedOptionsContainer.defaultProps = {
  updateOrderItem: undefined,
};

export default AdvancedOptionsContainer;
